import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import { NotificationActions } from '@next/core-lib/notification';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { FlockDataSeriesService } from 'src/app/shared/services/flock-dataseries-service';

import {
  DEFAULT_BREEDER_WEEK_REGISTRATION_FILTERS,
  DEFAULT_WEEK_REGISTRATION_FILTERS,
} from '../../../../assets/data/weekRegistrationFilters';
import { DeliveryLine } from '../../../delivery/models/delivery.model';
import { DeliveryService } from '../../../delivery/services/delivery.service';
import * as barnActions from '../../../settings/store/actions/barn.actions';
import { BreederFlockOverviewWeekKpiChartComponent } from '../../components/breeders/flock-overview-week-kpi-chart';
import { FlockOverviewWeekKpiChartComponent } from '../../components/flock-overview-week-kpi-chart/flock-overview-week-kpi-chart.component';
import {
  createDefaultFlockWeekChartFilters,
  CarbonFootprintResponse,
  FLOCK_VARIANT,
  Flock,
  FlockSettingsKeys,
  FlockWeekChartFilters,
  HenHatchery,
  PorphyrioCategories,
  RearingOrganization,
  WeekRegistrationFilters,
} from '../../models/flock.model';
import { FlockWeekDataSet } from '../../models/flock.model';
import {
  GetFarmResourcesResponse,
  UpsertFarmResourceResponse,
  UpsertFlockResponse,
} from '../../services/flock-service.model';
import { FlockService } from '../../services/flock.service';
import * as flockActions from '../actions/flock.actions';
import {
  getFlockWeekChartFilters,
  getFlockVariant,
  getSelectedFlockId,
  getSelectedFlockWeekData,
  getSelectedOtherFlockName,
  getSelectedOtherFlockWeekData,
} from '../selectors/flock.selectors';

const BREEDER_MORTALITY_CHART_GROUPS = [
  ['MortalityRate', 'HenMortalityRate', 'RoosterMortalityRate'],
  ['MortalityRateCumulative', 'HenMortalityRateCumulative', 'RoosterMortalityRateCumulative'],
];

const getBreederChartParameters = (parameter: string): string[] =>
  BREEDER_MORTALITY_CHART_GROUPS.find((group) => group.includes(parameter)) ?? [parameter];

@Injectable()
export class FlockEffects {
  constructor(
    private readonly actions$: Actions,
    private ngZone: NgZone,
    private router: Router,
    private readonly flockService: FlockService,
    private readonly deliveryService: DeliveryService,
    private readonly dialogService: DialogService,
    private readonly store: Store<RootState>,
    @Inject(LocaleService) private localeService: LocaleService,
    @Inject(BreakpointServiceInjector) private readonly breakpoint: BreakpointService,
    @Inject(SettingsService) private settingsService: SettingsService,
    private flockDataSeriesService: FlockDataSeriesService,
  ) {}

  loadData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadData),
      mergeMap(({ variant }) => {
        this.store.dispatch(flockActions.clearFlockState());
        this.store.dispatch(barnActions.clearBarnState());

        const actions: Action[] = [
          flockActions.setFlockVariant({ variant }),
          barnActions.loadBarnsRequest(),
          flockActions.loadSelectedFlockId(),
          flockActions.loadFlocksRequest({ variant }),
        ];

        return of(...actions);
      }),
    ),
  );

  loadFlocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFlocksRequest),
      mergeMap(({ variant }) =>
        this.flockService.getFlocks(variant).pipe(
          mergeMap((flocks: Flock[]) => of(flockActions.loadFlocksSuccess({ flocks }))),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadFlocksError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  selectInitialFlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFlocksSuccess),
      withLatestFrom(this.store.select(getSelectedFlockId)),
      switchMap(([{ flocks }, selectedFlockId]) => {
        if (flocks.length === 0) {
          return EMPTY;
        }

        const nextSelectedFlockId =
          flocks.find((flock) => flock.id === selectedFlockId)?.id ?? flocks[flocks.length - 1].id;

        return of(
          flockActions.setSelectedFlockId({
            id: nextSelectedFlockId,
            save: selectedFlockId !== nextSelectedFlockId,
            reloadWeekData: true,
          }),
        );
      }),
    ),
  );

  loadFlocksForAllLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFlocksForAllLocationsRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      mergeMap(([_, variant]) =>
        this.flockService.getFlocksForAllLocations(variant).pipe(
          mergeMap((flocksForAllLocations: Flock[]) =>
            of(flockActions.loadFlocksForAllLocationsSuccess({ flocksForAllLocations })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadFlocksForAllLocationsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFlock$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.upsertFlockRequest),
      withLatestFrom(
        this.store.select(getFlockWeekChartFilters),
        this.store.select(getFlockVariant),
      ),
      mergeMap(([request, flockWeekChartFilters, stateVariant]) => {
        const variant = request.variant ?? stateVariant;
        const { showOtherFlock, selectedOtherFlockId } = flockWeekChartFilters ?? {
          showOtherFlock: false,
          selectedOtherFlockId: undefined,
        };

        return this.flockService.upsertFlock(request.request, variant).pipe(
          switchMap((response: UpsertFlockResponse) => {
            const savedFlock: Flock = {
              departure: null,
              weekRegistrations: [],
              ...request.request,
              id: response.id,
            };

            if (!Object.prototype.hasOwnProperty.call(request.request, 'id')) {
              this.settingsService
                .setSettings({
                  contextName: FlockSettingsKeys.CONTEXT,
                  key: FlockSettingsKeys.NEW_FLOCK_ADDED,
                  storage: SettingsStorage.AppLocal,
                  data: response.id,
                })
                .subscribe();
            } else {
              this.settingsService
                .removeSettings({
                  contextName: FlockSettingsKeys.CONTEXT,
                  key: FlockSettingsKeys.NEW_FLOCK_ADDED,
                  storage: SettingsStorage.AppLocal,
                })
                .subscribe();
            }

            if (variant === FLOCK_VARIANT.Layer && showOtherFlock) {
              this.store.dispatch(
                flockActions.loadFlockWeekDataOfOtherFlockRequest({
                  otherFlockId: selectedOtherFlockId,
                }),
              );
            }

            const actions: Action[] = [
              flockActions.upsertFlockSuccess({ flock: savedFlock }),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('flock-saved'),
              }),
              flockActions.loadFlocksRequest({ variant }),
            ];

            if (variant === FLOCK_VARIANT.Layer) {
              actions.push(
                flockActions.loadFlockWeekDataOfFlockRequest({
                  flockId: response.id,
                }),
              );
            }

            return of(...actions);
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.upsertFlockError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  setSelectedFlockId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.setSelectedFlockId),
      switchMap((action) => {
        if (action.save) {
          this.settingsService
            .setSettings({
              contextName: FlockSettingsKeys.CONTEXT,
              key: FlockSettingsKeys.SELECTED_FLOCK_ID,
              storage: SettingsStorage.AppLocal,
              basedOnCurrentLocation: true,
              data: action.id,
            })
            .subscribe();
        }

        if (!action.reloadWeekData || action.id < 0) {
          return EMPTY;
        }

        return of(
          flockActions.loadFlockWeekDataOfFlockRequest({
            flockId: action.id,
          }),
        );
      }),
    ),
  );

  deleteFlockWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.deleteFlockWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({ text: this.localeService.translate('confirm-delete-flock') })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  flockActions.deleteFlockRequest({ id: action.id }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteFlockWeekRegistrationWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.deleteFlockWeekRegistrationWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            text: this.localeService.translate('confirm-delete-flock-week-registration'),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  flockActions.deleteFlockWeekRegistrationRequest({
                    flockId: action.flockId,
                    weekNumber: action.weekNumber,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteFlockRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.deleteFlockRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      mergeMap(([{ id }, variant]) =>
        this.flockService.deleteFlock(id, variant).pipe(
          switchMap(() => {
            this.settingsService
              .removeSettings({
                contextName: FlockSettingsKeys.CONTEXT,
                key: FlockSettingsKeys.SELECTED_FLOCK_ID,
                storage: SettingsStorage.AppLocal,
              })
              .subscribe();

            return of(
              flockActions.deleteFlockSuccess({ id }),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('flock-deleted'),
              }),
              flockActions.loadFlocksRequest({ variant }),
              flockActions.setSelectedFlockId({ id: -1, save: false, reloadWeekData: false }),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.deleteFlockError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteFlockSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(flockActions.deleteFlockSuccess),
        withLatestFrom(this.store.select(getFlockVariant)),
        tap(([_, variant]) => {
          const target = variant === FLOCK_VARIANT.Breeder ? '/breeders' : '/flock';
          this.ngZone.run(() => this.router.navigateByUrl(target)).then();
        }),
      ),
    { dispatch: false },
  );

  deleteFlockWeekRegistrationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.deleteFlockWeekRegistrationRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      mergeMap(([{ flockId, weekNumber }, variant]) =>
        this.flockService
          .deleteFlockWeekDataByFlockIdAdnWeekNumber(flockId, weekNumber, variant)
          .pipe(
            switchMap(() =>
              of(
                NotificationActions.successSnackbar({
                  message: this.localeService.translate('flock-week-registration-deleted'),
                }),
                flockActions.loadFlocksRequest({ variant }),
                flockActions.loadFlockWeekDataOfFlockRequest({ flockId }),
              ),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                flockActions.deleteFlockError({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  deleteFlockWeekRegistrationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(flockActions.deleteFlockWeekRegistrationSuccess),
        withLatestFrom(this.store.select(getFlockVariant)),
        tap(([_, variant]) => {
          const target = variant === FLOCK_VARIANT.Breeder ? '/breeders' : '/flock';
          this.ngZone.run(() => this.router.navigateByUrl(target)).then();
        }),
      ),
    { dispatch: false },
  );

  upsertBreederWeekRegistration$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.upsertBreederWeekRegistrationRequest),
      mergeMap(({ flockId, weekRegistration }) =>
        this.flockService.upsertBreederWeekRegistration(flockId, weekRegistration).pipe(
          switchMap(() =>
            of(
              flockActions.upsertBreederWeekRegistrationSuccess({
                flockId,
                weekRegistration,
              }),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('flock-saved'),
              }),
              flockActions.loadFlocksRequest({ variant: FLOCK_VARIANT.Breeder }),
              flockActions.loadFlockWeekDataOfFlockRequest({ flockId }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.upsertBreederWeekRegistrationError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadHatcheries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadHatcheriesRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      mergeMap(([_, variant]) =>
        this.flockService.getHatcheries(variant).pipe(
          map((hatcheries: HenHatchery[]) => flockActions.loadHatcheriesSuccess({ hatcheries })),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadHatcheriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadRearingOrganizations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadRearingOrganizationsRequest),
      mergeMap(() =>
        this.flockService.getRearingOrganizations().pipe(
          map((rearingOrganizations: RearingOrganization[]) =>
            flockActions.loadRearingOrganizationsSuccess({ rearingOrganizations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadRearingOrganizationsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadDeliveries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadDeliveryLinesRequest),
      withLatestFrom(this.store.select(UserSelectors.selectLocations)),
      exhaustMap(([_, userlocations]) => {
        const date = new Date();

        // Load deliveries from past two years
        date.setDate(date.getDate() - 730);
        const startDate = date.toISOString();
        const endDate = new Date().toISOString();
        const locations = userlocations.filter((l) => l.isSelected).map((l) => l.id);

        return this.deliveryService.getDeliveries(startDate, endDate, locations).pipe(
          map((deliveryLines: DeliveryLine[]) =>
            flockActions.loadDeliveryLinesSuccess({ deliveryLines }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadDeliveryLinesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  loadSelectedFlockId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadSelectedFlockId),
      switchMap((_) =>
        this.settingsService
          .getSingle<number>(
            {
              contextName: FlockSettingsKeys.CONTEXT,
              key: FlockSettingsKeys.SELECTED_FLOCK_ID,
              storage: SettingsStorage.AppLocal,
              basedOnCurrentLocation: true,
            },
            -1,
          )
          .pipe(
            switchMap((selectedFlockId: number) =>
              of(
                flockActions.setSelectedFlockId({
                  id: selectedFlockId,
                  save: false,
                  reloadWeekData: true,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadFlockWeekChartFilters$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFlockWeekChartFilters),
      switchMap((_) =>
        this.settingsService
          .getSingle<FlockWeekChartFilters>(
            {
              contextName: FlockSettingsKeys.CONTEXT,
              key: FlockSettingsKeys.FLOCK_WEEK_CHART_FILTERS,
              storage: SettingsStorage.AppLocal,
              basedOnCurrentLocation: true,
            },
            createDefaultFlockWeekChartFilters(),
          )
          .pipe(
            tap(({ showOtherFlock, selectedOtherFlockId }) => {
              if (showOtherFlock) {
                this.store.dispatch(
                  flockActions.loadFlockWeekDataOfOtherFlockRequest({
                    otherFlockId: selectedOtherFlockId,
                  }),
                );
              }
            }),
            switchMap((flockWeekChartFilters: FlockWeekChartFilters) =>
              of(flockActions.upsertFlockWeekChartFiltersSuccess({ flockWeekChartFilters })),
            ),
          ),
      ),
    ),
  );

  loadFlockBenchmarks$ = createEffect(
    () =>
      this.actions$
        .pipe(
          ofType(flockActions.loadFlockBenchmarks),
          withLatestFrom(this.store.select(getFlockWeekChartFilters)),
          mergeMap(([action, flockWeekChartFilters]) => {
            const resolvedFlockWeekChartFilters =
              flockWeekChartFilters ?? createDefaultFlockWeekChartFilters();

            return forkJoin([
              this.store.select(getSelectedFlockWeekData).pipe(take(1)),
              this.store.select(getSelectedOtherFlockWeekData).pipe(take(1)),
              this.store.select(getSelectedOtherFlockName).pipe(take(1)),
              this.breakpoint.ltSm$.pipe(take(1)),
              // benchmark 'WaterToFeedRation' returns a 400 Bad Request because there is no data for it,
              // so therefore we don't call this endpoint for 'WaterToFeedRatio'
              action.kpi.benchmark.benchmarkCode !== 'WaterToFeedRatio'
                ? this.flockService
                    .getBreedersReferenceBenchmarks(
                      action.kpi.benchmark.benchmarkCode,
                      action.flock.arrival.eggMarketingStandard,
                      action.flock.arrival.breed.name,
                    )
                    .pipe(
                      catchError((error: HttpErrorResponse) => {
                        if (error.status === 404) {
                          return of(flockActions.loadFlockBenchmarksError({ error }));
                        } else {
                          return of(
                            NotificationActions.errorSnackbar({
                              message: error.message,
                            }),
                          );
                        }
                      }),
                    )
                : of(<DataSeries<number, number>>{}),
              this.flockService
                .getPorphyrioBenchmarks(
                  action.kpi.benchmark.benchmarkCode,
                  resolvedFlockWeekChartFilters.breedCategoryName,
                  resolvedFlockWeekChartFilters.eggMarketingStandard,
                  resolvedFlockWeekChartFilters.breedName,
                )
                .pipe(
                  catchError((error: HttpErrorResponse) => {
                    if (error.status === 404) {
                      return of(flockActions.loadFlockBenchmarksError({ error }));
                    } else {
                      return of(
                        NotificationActions.errorSnackbar({
                          message: error.message,
                        }),
                      );
                    }
                  }),
                ),
            ]).pipe(
              map(
                ([
                  flockWeekData,
                  otherFlockWeekData,
                  otherFlockName,
                  isMobile,
                  breedersReferenceResponse,
                  porphyrioResponse,
                ]) => {
                  let flockWeekDataSeries: DataSeries<number, number> = null;
                  let breedersReferenceSeries: DataSeries<number, number> = null;
                  let porphyrioSeries: DataSeries<number, number> = null;
                  let otherFlockWeekDataSeries: DataSeries<number, number> = null;

                  if (
                    resolvedFlockWeekChartFilters.showCurrentFlock &&
                    !!flockWeekData?.dataSeries
                  ) {
                    flockWeekDataSeries = this.flockDataSeriesService.filterDataSeries(
                      action.flock.id,
                      flockWeekData.dataSeries,
                      action.kpi.name,
                    );
                  }
                  if (
                    (breedersReferenceResponse as DataSeries<number, number>).series &&
                    resolvedFlockWeekChartFilters.showBreedersReferenceBenchmark
                  ) {
                    breedersReferenceSeries = breedersReferenceResponse as DataSeries<
                      number,
                      number
                    >;
                  }
                  if (
                    (porphyrioResponse as DataSeries<number, number>).series &&
                    resolvedFlockWeekChartFilters.showPorphyrioBenchmark
                  ) {
                    porphyrioSeries = porphyrioResponse as DataSeries<number, number>;
                  }
                  if (
                    resolvedFlockWeekChartFilters.showOtherFlock &&
                    !!otherFlockWeekData?.dataSeries
                  ) {
                    otherFlockWeekDataSeries = this.flockDataSeriesService.filterDataSeries(
                      resolvedFlockWeekChartFilters.selectedOtherFlockId,
                      otherFlockWeekData.dataSeries,
                      action.kpi.name,
                    );
                  }

                  this.dialogService.fullscreenDialog(
                    FlockOverviewWeekKpiChartComponent,
                    {
                      kpi: action.kpi,
                      flock: action.flock,
                      flockWeekDataSeries,
                      breedersReferenceBenchmarks: breedersReferenceSeries,
                      porphyrioBenchmarks: porphyrioSeries,
                      otherFlockWeekDataSeries,
                      otherFlockName,
                      flockWeekChartFilters: resolvedFlockWeekChartFilters,
                    },
                    {
                      width: isMobile ? '100vw' : '50vw',
                    },
                  );
                  return EMPTY;
                },
              ),
            );
          }),
        )
        .pipe(
          startWith(() => this.store.dispatch(CoreActions.setLoading({ loading: true }))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
    {
      dispatch: false,
    },
  );

  loadBreederFlockChart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(flockActions.loadBreederFlockChart),
        mergeMap((action) =>
          forkJoin([
            this.store.select(getSelectedFlockWeekData).pipe(take(1)),
            this.breakpoint.ltSm$.pipe(take(1)),
          ]).pipe(
            tap(([flockWeekData, isMobile]) => {
              const chartParameters = getBreederChartParameters(action.kpi.name);
              const chartKpiName = chartParameters[0];
              const flockWeekDataSeries = flockWeekData?.dataSeries
                ? this.flockDataSeriesService.filterDataSeries(
                    action.flock.id,
                    flockWeekData.dataSeries,
                    chartKpiName,
                    chartParameters.slice(1),
                  )
                : null;

              this.dialogService.fullscreenDialog(
                BreederFlockOverviewWeekKpiChartComponent,
                {
                  kpi: {
                    ...action.kpi,
                    name: chartKpiName,
                    translationKey: `flock-overview.week-registrations.breeder-kpis.${chartKpiName}`,
                  },
                  flock: action.flock,
                  flockWeekDataSeries,
                },
                {
                  width: isMobile ? '100vw' : '50vw',
                },
              );
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadFlockWeekDataOfFlock$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFlockWeekDataOfFlockRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      switchMap(([{ flockId }, variant]) =>
        this.flockService.getFlockWeekDataByFlockId(flockId, variant).pipe(
          switchMap((data: FlockWeekDataSet) =>
            of(flockActions.loadFlockWeekDataOfFlockSuccess({ data })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadFlockWeekDataOfOtherFlock$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFlockWeekDataOfOtherFlockRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      switchMap(([{ otherFlockId }, variant]) =>
        this.flockService.getFlockWeekDataByFlockId(otherFlockId, variant).pipe(
          switchMap((data: FlockWeekDataSet) =>
            of(flockActions.loadFlockWeekDataOfOtherFlockSuccess({ data })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(NotificationActions.errorSnackbar({ message: error.message })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadPorphyrioCategories = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadPorphyrioCategoriesRequest),
      mergeMap(() =>
        this.flockService.getPorphyrioCategories().pipe(
          mergeMap((porphyrioCategories: PorphyrioCategories) =>
            of(flockActions.loadPorphyrioCategoriesSuccess({ porphyrioCategories })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFlockWeekChartFilters$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.upsertFlockWeekChartFiltersRequest),
      map((action) => action.flockWeekChartFilters),
      switchMap((flockWeekChartFilters) => {
        this.settingsService
          .setSettings({
            contextName: FlockSettingsKeys.CONTEXT,
            key: FlockSettingsKeys.FLOCK_WEEK_CHART_FILTERS,
            storage: SettingsStorage.AppLocal,
            basedOnCurrentLocation: true,
            data: { ...flockWeekChartFilters },
          })
          .subscribe();

        if (flockWeekChartFilters.showOtherFlock) {
          this.store.dispatch(
            flockActions.loadFlockWeekDataOfOtherFlockRequest({
              otherFlockId: flockWeekChartFilters.selectedOtherFlockId,
            }),
          );
        } else {
          this.store.dispatch(flockActions.clearFlockWeekDataOfOtherFlock());
        }

        return of(flockActions.upsertFlockWeekChartFiltersSuccess({ flockWeekChartFilters }));
      }),
    ),
  );

  loadWeekRegistrationFilters$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadWeekRegistrationsRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      switchMap(([_, variant]) => {
        const isBreeder = variant === FLOCK_VARIANT.Breeder;
        return this.settingsService
          .getSingle<WeekRegistrationFilters>(
            {
              contextName: FlockSettingsKeys.CONTEXT,
              key: isBreeder
                ? FlockSettingsKeys.BREEDER_FLOCK_WEEK_REGISTRATION_FILTERS
                : FlockSettingsKeys.FLOCK_WEEK_REGISTRATION_FILTERS,
              storage: SettingsStorage.AppLocal,
              basedOnCurrentLocation: true,
            },
            isBreeder
              ? DEFAULT_BREEDER_WEEK_REGISTRATION_FILTERS
              : DEFAULT_WEEK_REGISTRATION_FILTERS,
          )
          .pipe(
            switchMap((weekRegistrationFilters: WeekRegistrationFilters) =>
              of(flockActions.upsertWeekRegistrationFiltersSuccess({ weekRegistrationFilters })),
            ),
          );
      }),
    ),
  );

  upsertWeekRegistrationFilters$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.upsertWeekRegistrationFiltersRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      switchMap(([{ weekRegistrationFilters }, variant]) => {
        this.settingsService
          .setSettings({
            contextName: FlockSettingsKeys.CONTEXT,
            key:
              variant === FLOCK_VARIANT.Breeder
                ? FlockSettingsKeys.BREEDER_FLOCK_WEEK_REGISTRATION_FILTERS
                : FlockSettingsKeys.FLOCK_WEEK_REGISTRATION_FILTERS,
            storage: SettingsStorage.AppLocal,
            data: { ...weekRegistrationFilters },
            basedOnCurrentLocation: true,
          })
          .subscribe();

        return of(flockActions.upsertWeekRegistrationFiltersSuccess({ weekRegistrationFilters }));
      }),
    ),
  );

  resetWeekRegistrationFilters$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.resetWeekRegistrationFiltersRequest),
      withLatestFrom(this.store.select(getFlockVariant)),
      map(([_, variant]) =>
        flockActions.upsertWeekRegistrationFiltersRequest({
          weekRegistrationFilters:
            variant === FLOCK_VARIANT.Breeder
              ? DEFAULT_BREEDER_WEEK_REGISTRATION_FILTERS
              : DEFAULT_WEEK_REGISTRATION_FILTERS,
        }),
      ),
    ),
  );

  loadIsDetailedViewSelected$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadIsDetailedViewSelectedRequest),
      switchMap(() =>
        this.settingsService
          .getSingle<boolean>(
            {
              contextName: FlockSettingsKeys.CONTEXT,
              key: FlockSettingsKeys.FLOCK_WEEK_REGISTRATION_DETAILED,
              storage: SettingsStorage.AppLocal,
              basedOnCurrentLocation: true,
            },
            false,
          )
          .pipe(
            switchMap((isDetailedViewSelected: boolean) =>
              of(flockActions.setIsDetailedViewSelectedSuccess({ isDetailedViewSelected })),
            ),
          ),
      ),
    ),
  );

  setIsDetailedViewSelected$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.setIsDetailedViewSelectedRequest),
      map((action) => action.isDetailedViewSelected),
      switchMap((isDetailedViewSelected) => {
        this.settingsService
          .setSettings({
            contextName: FlockSettingsKeys.CONTEXT,
            key: FlockSettingsKeys.FLOCK_WEEK_REGISTRATION_DETAILED,
            storage: SettingsStorage.AppLocal,
            data: isDetailedViewSelected,
            basedOnCurrentLocation: true,
          })
          .subscribe();

        return of(flockActions.setIsDetailedViewSelectedSuccess({ isDetailedViewSelected }));
      }),
    ),
  );

  loadFarmResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadFarmResourcesRequest),
      switchMap(() =>
        this.flockService.getFarmResources().pipe(
          map((response: GetFarmResourcesResponse) =>
            flockActions.loadFarmResourcesSuccess({
              farmResources: response.farmResources,
              flockResources: response.flockResources,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadFarmResourcesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFarmResource$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.upsertFarmResourceRequest),
      mergeMap((request) =>
        this.flockService.upsertFarmResource(request.request).pipe(
          switchMap((response: UpsertFarmResourceResponse) =>
            of(
              flockActions.upsertFarmResourceSuccess({
                farmResource: { ...request.request, id: response.id },
              }),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('sustainability.farm-resource-saved'),
              }),
              flockActions.loadFarmResourcesRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.upsertFarmResourceError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteFarmResourceWithConfirmationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.deleteFarmResourceWithConfirmationRequest),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            text: this.localeService.translate('sustainability.confirm-delete-farm-resource'),
          })
          .pipe(
            switchMap((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                return [flockActions.deleteFarmResourceRequest({ id: action.id })];
              }
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  deleteFarmResourceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.deleteFarmResourceRequest),
      mergeMap(({ id }) =>
        this.flockService.deleteFarmResource(id).pipe(
          switchMap(() =>
            of(
              flockActions.deleteFarmResourceSuccess({ id }),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('sustainability.farm-resource-deleted'),
              }),
              flockActions.loadFarmResourcesRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.deleteFarmResourceError({ error }),
              NotificationActions.successSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadCarbonFootprintResult$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.loadCarbonFootprintRequest),
      switchMap(({ flockId }) =>
        this.flockService.getCarbonFootprint(flockId).pipe(
          switchMap((data: CarbonFootprintResponse) =>
            of(flockActions.loadCarbonFootprintSuccess({ data })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              flockActions.loadCarbonFootprintError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  calculateCarbonFootprintResult$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.calculateCarbonFootprintRequest),
      mergeMap(({ flockId }) => {
        return this.flockService.calculateCarbonFootprint(flockId).pipe(
          delay(70000),
          map(() => flockActions.loadCarbonFootprintRequest({ flockId })),
          catchError((error: HttpErrorResponse) => {
            let errorTitle = this.localeService.translate(
              'sustainability.carbon-footprint-title-error',
            );
            let errorMessages = error.error?.errorMessages ?? error.message;

            if (error.status === 500) {
              errorMessages = this.localeService.translate(
                'sustainability.carbon-footprint-500-error-message',
              );
            }

            this.dialogService.confirmWarning({
              title: errorTitle,
              text: errorMessages,
            });

            return of(flockActions.calculateCarbonFootprintFailure({ error: errorMessages }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  refreshCarbonFootprintResult$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(flockActions.refreshCarbonFootprintRequest),
      mergeMap(({ flockId }) =>
        this.flockService.refreshCarbonFootprint(flockId).pipe(
          map(() => flockActions.loadCarbonFootprintRequest({ flockId })),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = error.error?.errorMessage ?? error.message;

            this.dialogService.confirmWarning({
              title: this.localeService.translate(
                'sustainability.carbon-footprint-refresh-error-title',
              ),
              text: errorMessage,
            });

            return of(flockActions.refreshCarbonFootprintFailure({ error: errorMessage }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
